import type { AccountInfo } from '@azure/msal-browser';
import LogRocket from 'logrocket';

import { appConfig } from './appConfig';

/**
 * Tie the LogRocket session to the signed-in provider so a replay can be
 * found from a support ticket by name or email.
 *
 * The user id is the Entra object id (`localAccountId`, the token `oid`
 * claim), which is stable across renames. Traits are the provider's display
 * name, email/UPN, NPI, and the environment label (`appConfig.envName`).
 * The identity fields describe the clinician using the app, not
 * a patient, so they are not PHI and may be sent (SEC-199 covers PHI; see
 * the LogRocket notes in main.tsx). Nothing from the dashboard payloads is
 * ever attached here.
 *
 * Idempotent per account: MSAL can set the active account more than once per
 * page load (redirect result, then LOGIN_SUCCESS), and re-identifying with the
 * same id is a no-op for LogRocket but noisy in the console.
 */

const OID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NPI_RE = /^\d{10}$/;
const NPI_EXTENSION_CLAIM_RE = /^extension_[0-9a-f]{32}_npi$/i;

let identified: string | null = null;

export interface TelemetryIdentity {
  uid: string;
  traits: { name?: string; email?: string; npi?: string; env: string };
}

/** NPI from the ID token claims, mirroring useAuth.npiFromClaims. */
function npiFromAccount(account: AccountInfo): string | undefined {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  const raw: unknown[] = [claims['extn.npi']];
  for (const [key, value] of Object.entries(claims)) {
    if (NPI_EXTENSION_CLAIM_RE.test(key)) raw.push(value);
  }
  for (const candidate of raw.flatMap((v) => (Array.isArray(v) ? v : [v]))) {
    if (typeof candidate === 'string' && NPI_RE.test(candidate.trim())) return candidate.trim();
  }
  return undefined;
}

/** Stable user id plus provider traits for telemetry, or null when the account carries no id. */
export function telemetryIdentity(
  account: AccountInfo | null | undefined,
): TelemetryIdentity | null {
  const oid = account?.localAccountId?.trim();
  if (!account || !oid || !OID_RE.test(oid)) return null;
  // `env` mirrors the session-level `release` so the user page and user
  // filters can distinguish local/staging/production too.
  const traits: TelemetryIdentity['traits'] = { env: appConfig.envName };
  const name = account.name?.trim();
  const email = account.username?.trim();
  const npi = npiFromAccount(account);
  if (name) traits.name = name;
  if (email) traits.email = email;
  if (npi) traits.npi = npi;
  return { uid: oid.toLowerCase(), traits };
}

export function identifyLogRocketUser(account: AccountInfo | null | undefined): void {
  const identity = telemetryIdentity(account);
  if (!identity || identity.uid === identified) return;
  try {
    LogRocket.identify(identity.uid, identity.traits);
    identified = identity.uid;
  } catch (err) {
    // Telemetry must never break sign-in.
    console.warn('[telemetry] LogRocket.identify failed', err);
  }
}

/** Test seam: forget the last identified user. */
export function resetLogRocketIdentity(): void {
  identified = null;
}
